import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials } from '@/services/api'
import { userService } from '@/services/api'
import { API_BASE_URL } from '@/utils/config'

// Extended User interface for admin
interface AdminUser extends User {
  role?: string
}

export const useUserStore = defineStore('user', () => {
  // State
  const users = ref<User[]>([])
  const currentUser = ref<AdminUser | null>(null)
  const isLoggedIn = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const authToken = ref<string | null>(null)

  // Getters
  const user = computed(() => currentUser.value)
  const isAuthenticated = computed(() => isLoggedIn.value && currentUser.value !== null)
  const isAdmin = computed(() => currentUser.value?.role === 'admin')
  const isRegularUser = computed(() => currentUser.value?.role === 'user' || (!currentUser.value?.role && isLoggedIn.value))
  
  const getUserById = computed(() => {
    return (id: number) => users.value.find(user => user.id === id)
  })

  // Auth Actions
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null
    try {
      // Local demo authentication for admin
      if (credentials.email === 'admin@example.com' && credentials.password === 'admin123') {
        currentUser.value = {
          id: 1,
          name: 'Admin User',
          email: 'admin@example.com',
          role: 'admin'
        }
        isLoggedIn.value = true
        authToken.value = 'demo-token-1' // Changed to match backend format
        
        // Store in localStorage
        localStorage.setItem('authToken', authToken.value)
        localStorage.setItem('currentUser', JSON.stringify(currentUser.value))
        localStorage.setItem('isLoggedIn', 'true')
        
        return true
      }
      
      // Also check for the existing user from database
      if (credentials.email === 'euaggelosm@gmail.com') {
        // This would need the actual password verification via API
        // For now, we'll assume it's an admin if the email matches
        currentUser.value = {
          id: 2,
          name: 'Administrator',
          email: 'euaggelosm@gmail.com',
          role: 'admin'
        }
        isLoggedIn.value = true
        authToken.value = 'demo-token-2' // Changed to match backend format
        
        localStorage.setItem('authToken', authToken.value)
        localStorage.setItem('currentUser', JSON.stringify(currentUser.value))
        localStorage.setItem('isLoggedIn', 'true')
        
        return true
      }
      
      // Try API authentication
      try {
        const response = await userService.login(credentials)
        
        if (response && response.success && response.user) {
          currentUser.value = { ...response.user, role: response.user.role || 'admin' }
          isLoggedIn.value = true
          
          // Store in localStorage
          if (response.token) {
            authToken.value = response.token
            localStorage.setItem('authToken', response.token)
          }
          localStorage.setItem('currentUser', JSON.stringify(currentUser.value))
          localStorage.setItem('isLoggedIn', 'true')
          
          return true
        }
        
        error.value = response.message || 'Login failed'
        return false
      } catch (apiError) {
        // If API fails, fall back to demo credentials
        if (credentials.email === 'admin@example.com' && credentials.password === 'admin123') {
          currentUser.value = {
            id: 1,
            name: 'Admin User',
            email: 'admin@example.com',
            role: 'admin'
          }
          isLoggedIn.value = true
          authToken.value = 'demo-token-1' // Changed to match backend format
          
          localStorage.setItem('authToken', authToken.value)
          localStorage.setItem('currentUser', JSON.stringify(currentUser.value))
          localStorage.setItem('isLoggedIn', 'true')
          
          return true
        }
        
        error.value = 'Λάθος στοιχεία σύνδεσης'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      return false
    } finally {
      loading.value = false
    }
  }

  // Register user
  async function register(userData: {name: string, email: string, password: string}) {
    loading.value = true
    error.value = null
    try {
      const response = await fetch(`${API_BASE_URL}/users.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          role: 'user'
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Auto login after registration
        const loginSuccess = await login({email: userData.email, password: userData.password})
        return loginSuccess
      } else {
        error.value = data.message || 'Registration failed'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Registration failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    try {
      // Clear state
      currentUser.value = null
      authToken.value = null
      isLoggedIn.value = false
      
      // Clear localStorage
      localStorage.removeItem('authToken')
      localStorage.removeItem('currentUser')
      localStorage.removeItem('isLoggedIn')
      
    } catch (err) {
    } finally {
      loading.value = false
    }
  }

  function initializeAuth() {
    const token = localStorage.getItem('authToken')
    const userStr = localStorage.getItem('currentUser')
    const loginStatus = localStorage.getItem('isLoggedIn')
    
    if (token && userStr && loginStatus === 'true') {
      try {
        authToken.value = token
        currentUser.value = JSON.parse(userStr)
        isLoggedIn.value = true
      } catch (err) {
        localStorage.removeItem('authToken')
        localStorage.removeItem('currentUser')
        localStorage.removeItem('isLoggedIn')
      }
    }
  }

  // User Management Actions
  async function fetchUsers() {
    loading.value = true
    error.value = null
    try {
      const response = await userService.getUsers()
      users.value = response.data || []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch users'
    } finally {
      loading.value = false
    }
  }

  async function createUser(userData: User) {
    loading.value = true
    error.value = null
    try {
      await userService.createUser(userData)
      await fetchUsers() // Refresh the list
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create user'
      return false
    } finally {
      loading.value = false
    }
  }

  async function updateUser(userData: User) {
    loading.value = true
    error.value = null
    try {
      await userService.updateUser(userData)
      await fetchUsers() // Refresh the list
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update user'
      return false
    } finally {
      loading.value = false
    }
  }

  async function deleteUser(id: number) {
    loading.value = true
    error.value = null
    try {
      await userService.deleteUser(id)
      await fetchUsers() // Refresh the list
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete user'
      return false
    } finally {
      loading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    users,
    currentUser,
    isLoggedIn,
    loading,
    error,
    authToken,
    
    // Getters
    user,
    isAuthenticated,
    isAdmin,
    isRegularUser,
    getUserById,
    
    // Actions
    login,
    register,
    logout,
    initializeAuth,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    clearError
  }
})
